import { useCallback, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import PDFPageSelectionControls from './pdf-page-selection-controls/PDFPageSelectionControls';
import { PDFViewerTabProps } from './PDFViewerTab.props';
import { ApiCreateDocumentGenerationJob, ApiService, FileModel } from '../../../../api';
import { RetrieveDocumentGenerationJob } from '../../../../api/models/RetrieveDocumentGenerationJob';
import { AI_PDF_PROCESSING_ENABLER } from '../../../../common/constants/features';
import { useLayoutContext } from '../../../../context/LayoutContext';
import { GenerationJobParsingMode } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-parsing-mode.enum';
import { GenerationJobState } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { useDocuments } from '../../../../services/hooks';
import { Logger } from '../../../../utilities';
import useCheckFeatures from '../../../FeatureEnabler/hooks';
import PDFViewer from '../../../FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode } from '../../../FileViewer/pdf-viewer/PDFViewer.props';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from '../document-preview.constants';
import { PDFJobRibbon } from './pdf-job-ribbon/PDFJobRibbon';

const PDFViewerTab = ({ document, companyId }: PDFViewerTabProps) => {
  const { apiCompanyDocumentGenerationJobsNewCreate, apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList } =
    ApiService;
  const { currentMeasurementDate } = useLayoutContext();
  const { downloadDocumentContent } = useDocuments();
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [allowPageSelection, setAllowPageSelection] = useState(false);
  const [processingJob, setProcessingJob] = useState<RetrieveDocumentGenerationJob>();
  const [, checkFeature] = useCheckFeatures();
  const [genJobProcessing, setGenJobProcessing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['document', document.document_reference_id, 'content'],
    enabled: !!document.document_reference_id,
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(document),
  });

  const handlePageToggle = (pageNumber: number) => {
    setSelectedPages(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(pageNumber)) {
        newSelected.delete(pageNumber);
      } else {
        newSelected.add(pageNumber);
      }
      return newSelected;
    });
  };

  const handleProcessPages = useCallback(async () => {
    if (!document.file?.id || !companyId) {
      Logger.warn('Missing required data: file ID or company ID');
      return;
    }

    try {
      setGenJobProcessing(true);
      const results = await apiCompanyDocumentGenerationJobsNewCreate(companyId, {
        reference_files: [document.file.id],
        processing_type: ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
        desired_termination_state: GenerationJobState.PENDING_DOCUMENT_CHUNKING,
        parser_overrides: [
          {
            file_type: FileModel.file_type.PDF,
            page_numbers: Array.from(selectedPages),
            pdf_parsing_mode: GenerationJobParsingMode.TABULAR,
            convert_parsed_results_to_xlsx: true,
          },
        ],
      });

      setProcessingJob(results);
    } catch (error) {
      Logger.error('Error processing pages:', error);
      setGenJobProcessing(false);
    }
  }, [apiCompanyDocumentGenerationJobsNewCreate, companyId, document.file?.id, selectedPages]);

  const handleClearSelection = () => {
    setSelectedPages(new Set());
  };

  const toggleSelectionMode = (enabled: boolean) => {
    setAllowPageSelection(enabled);
    if (!enabled) {
      setSelectedPages(new Set());
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data.content && !data.blob_url) {
    return <div>No document found: Current Document Preview size limit is 5MB</div>;
  }

  return (
    <Box>
      {/* Job Status Display */}
      {processingJob && currentMeasurementDate && (
        <PDFJobRibbon generationJob={processingJob} measurementDate={currentMeasurementDate} />
      )}

      <PDFPageSelectionControls
        allowPageSelection={allowPageSelection}
        selectedPages={selectedPages}
        onToggleSelectionMode={toggleSelectionMode}
        onPageToggle={handlePageToggle}
        onClearSelection={handleClearSelection}
        onProcessPages={handleProcessPages}
        genJobProcessing={genJobProcessing}
      />

      {/* PDF Viewer */}
      <PDFViewer
        base64Content={data.content}
        src={data.blob_url}
        allowPageSelection={allowPageSelection}
        selectedPages={selectedPages}
        viewMode={allowPageSelection ? PDFViewerViewMode.THUMBNAIL : PDFViewerViewMode.DOCUMENT}
        onPageToggle={handlePageToggle}
      />
    </Box>
  );
};

export default PDFViewerTab;
